import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

function SupplyHealthRing({ targetScore = 97, animate = false }: { targetScore?: number; animate?: boolean }) {
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let frame: number;
    const duration = 2500;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(eased * targetScore));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, targetScore]);
  const circumference = 2 * Math.PI * 90;
  return (
    <div className="supply-ring" style={{ '--score': animate ? targetScore : 0 } as React.CSSProperties}>
      <svg width="220" height="220" viewBox="0 0 200 200">
        <defs>
          <linearGradient id="linkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="50%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#fcd34d" />
          </linearGradient>
        </defs>
        <circle className="ring-track" cx="100" cy="100" r="90" />
        <circle className="ring-fill" cx="100" cy="100" r="90"
          style={{ strokeDasharray: circumference, strokeDashoffset: animate ? circumference - (circumference * targetScore) / 100 : circumference, transition: 'stroke-dashoffset 2.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />
      </svg>
      <div className="supply-ring-value">
        <div className="stat-counter" style={{ fontSize: '3rem', fontWeight: 800, color: '#fff' }}>{displayScore}%</div>
        <div style={{ fontSize: '0.85rem', color: 'rgba(251, 191, 36, 0.7)', fontWeight: 500, marginTop: 4 }}>Supply Health</div>
      </div>
    </div>
  );
}

function SupplyChainDemo() {
  const [phase, setPhase] = useState<'idle' | 'scanning' | 'complete'>('idle');
  const [visibleAlerts, setVisibleAlerts] = useState<number[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  const alerts = [
    { icon: '📦', category: 'Inventory', alert: 'Chicago warehouse at 23% capacity — auto-reorder triggered for 5 SKUs.', color: '#f59e0b' },
    { icon: '🚢', category: 'Shipping', alert: 'Port of Long Beach congestion detected. Rerouting 3 shipments via Oakland.', color: '#ef4444' },
    { icon: '🏭', category: 'Supplier', alert: 'Apex Manufacturing Inc. lead time increased 15%. Alternative supplier scored 94/100.', color: '#3b82f6' },
    { icon: '📊', category: 'Forecast', alert: 'Q3 demand surge predicted: +34% for electronics category. Pre-order recommended.', color: '#10b981' },
  ];

  const startDemo = useCallback(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    setPhase('scanning');
    setTimeout(() => {
      setPhase('complete');
      alerts.forEach((_, idx) => {
        setTimeout(() => setVisibleAlerts(prev => [...prev, idx]), idx * 500);
      });
    }, 1500);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setTimeout(startDemo, 400); }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startDemo]);

  return (
    <div ref={ref} style={{ width: '100%', maxWidth: 700, margin: '0 auto' }}>
      <div className="glass-card" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 12, borderBottom: '1px solid rgba(245,158,11,0.15)' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fbbf24', animation: 'floatUpDown 2s ease-in-out infinite' }} />
          <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 500 }}>AI Supply Chain Agent — Live Monitoring</span>
        </div>
        {phase === 'scanning' && (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.2rem', color: '#fbbf24', fontWeight: 600 }}>🔍 Scanning supply chain...</div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 8 }}>AI is monitoring suppliers, inventory, and logistics across 12 locations</div>
          </div>
        )}
        {phase === 'complete' && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {alerts.map((item, idx) => (
              visibleAlerts.includes(idx) && (
                <div key={item.category} className="supply-animate" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px', borderRadius: 12, background: 'rgba(30, 37, 86, 0.4)', border: `1px solid ${item.color}20` }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${item.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: item.color, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{item.category}</div>
                    <p style={{ fontSize: '0.85rem', color: '#e2e8f0', margin: 0, lineHeight: 1.5 }}>{item.alert}</p>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatsCounter({ end, suffix = '', prefix = '' }: { end: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!inView) return;
    let frame: number;
    const duration = 2000;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * end));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, end]);
  return <span ref={ref} className="stat-counter">{prefix}{count.toLocaleString()}{suffix}</span>;
}

const FEATURES = [
  { icon: '🤖', title: 'AI Supply Chain Agent', description: 'Ask questions about your supply chain in plain English. Get real-time, data-driven answers instantly.', gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)' },
  { icon: '📦', title: 'Smart Inventory', description: 'AI predicts demand and auto-triggers reorders. Never run out of stock or overstock again.', gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)' },
  { icon: '🏭', title: 'Supplier Intelligence', description: 'Score and rank suppliers by reliability, cost, and lead time. AI recommends alternatives instantly.', gradient: 'linear-gradient(135deg, #10b981, #34d399)' },
  { icon: '🚢', title: 'Logistics Optimization', description: 'Real-time shipment tracking with AI-powered rerouting when disruptions are detected.', gradient: 'linear-gradient(135deg, #ef4444, #f87171)' },
  { icon: '📊', title: 'Demand Forecasting', description: '94% accurate demand predictions using ML. Plan procurement weeks ahead of the curve.', gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' },
  { icon: '🔗', title: 'Multi-Location Sync', description: 'Manage warehouses, stores, and distribution centers across the US from one intelligent dashboard.', gradient: 'linear-gradient(135deg, #ec4899, #f472b6)' },
];

export default function EFLinkLanding() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [heroInView, setHeroInView] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setHeroInView(true); }, { threshold: 0.1 });
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-root">
      <nav className={scrollY > 50 ? 'nav-glass' : ''} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, transition: 'all 0.3s ease', ...(scrollY <= 50 ? { background: 'transparent' } : {}) }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>🔗</div>
            <div>
              <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>EF-Link</span>
              <span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: 8 }}>by Executive Force AI</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 16px', fontSize: '0.85rem', fontWeight: 500, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600, color: '#fff', background: '#f59e0b', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Start Free Trial</button>
          </div>
        </div>
      </nav>

      <section ref={heroRef} className="grid-pattern" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5rem 1.5rem 3rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--1" /><div className="landing-orb landing-orb--2" /><div className="landing-orb landing-orb--3" /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.2s', marginBottom: 24 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.2)' }}>🔗 AI-Powered Supply Chain Intelligence</span>
          </div>
          <h1 style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: 24, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.4s' }}>
            <span style={{ color: '#fff' }}>Your AI</span><br />
            <span className="text-gradient-link">Supply Chain Commander</span>
          </h1>
          <p style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)', color: '#94a3b8', maxWidth: 600, margin: '0 auto 3rem', lineHeight: 1.6, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 0.6s' }}>
            AI that monitors suppliers, predicts disruptions, and keeps your <span style={{ color: '#fcd34d', fontWeight: 600 }}>supply chain resilient</span>.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48, opacity: heroInView ? 1 : 0, transform: heroInView ? 'scale(1)' : 'scale(0.8)', transition: 'all 0.8s ease 0.8s' }}>
            <div className="animate-float"><SupplyHealthRing targetScore={97} animate={heroInView} /></div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 1.0s' }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>Start 7-Day Free Trial 🚀</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 16 }}>Live AI Demo</span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Your AI <span className="text-gradient-link">Never Sleeps</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>Watch as AI monitors your entire supply chain, flags risks, and recommends actions in real-time.</p>
          </div>
          <SupplyChainDemo />
        </div>
      </section>

      <div className="section-divider" />

      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Supply Chain <span className="text-gradient-link">Superpowers</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>Everything you need to build a resilient supply chain. Nothing you don't.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {FEATURES.map((f) => (
              <div key={f.title} className="glass-card" style={{ padding: '1.5rem' }}>
                <div className="feature-icon-glow">
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: f.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', marginBottom: 16 }}>{f.icon}</div>
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: '0.9rem', color: '#94a3b8', lineHeight: 1.6, margin: 0 }}>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      <section style={{ position: 'relative', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 32, textAlign: 'center' }}>
          {[
            { value: 97, suffix: '%', label: 'Supply Uptime' },
            { value: 34, suffix: '%', label: 'Cost Reduction' },
            { value: 500, suffix: '+', label: 'AI Queries/Month' },
            { value: 12, suffix: '', label: 'Locations Monitored' },
          ].map((s) => (
            <div key={s.label}>
              <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#fff', marginBottom: 4 }}><StatsCounter end={s.value} suffix={s.suffix} /></div>
              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="section-divider" />

      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Simple <span className="text-gradient-link">Pricing</span></h2>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', marginBottom: 40 }}>7-day free trial. No credit card required. Cancel anytime.</p>
          <div className="glass-card" style={{ padding: '2.5rem', borderColor: 'rgba(245,158,11,0.4)' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>EF-Link</div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
              <span style={{ fontSize: '3.5rem', fontWeight: 800, color: '#fff' }}>$49</span>
              <span style={{ fontSize: '1.1rem', color: '#64748b' }}>/month</span>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: 24 }}>500 AI queries · Supply chain monitoring · Demand forecasting</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', textAlign: 'left' }}>
              {['AI supply chain agent', '500 AI queries per month', 'Real-time inventory monitoring', 'Supplier scoring & intelligence', 'Demand forecasting (94% accuracy)', 'Multi-location management'].map((item) => (
                <li key={item} style={{ fontSize: '0.9rem', color: '#cbd5e1', padding: '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ color: '#fbbf24' }}>✓</span> {item}</li>
              ))}
            </ul>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ width: '100%', padding: '14px', fontSize: '1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start 7-Day Free Trial</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--2" style={{ bottom: '20%', left: '10%' }} /><div className="landing-orb landing-orb--3" style={{ top: '10%', right: '10%' }} /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, marginBottom: 24 }}>Ready to <span className="text-gradient-link">Link Your Supply Chain</span>?</h2>
          <p style={{ fontSize: '1.2rem', color: '#94a3b8', marginBottom: 40 }}>Join businesses reducing supply chain costs by 34% — for just $49/month.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start Free Trial →</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      <footer style={{ borderTop: '1px solid rgba(245,158,11,0.15)', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' }}>🔗</div>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Executive Force AI</span>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#475569', margin: 0 }}>© {new Date().getFullYear()} Executive Force AI Inc. All rights reserved.</p>
          <div style={{ display: 'flex', gap: 24 }}>
            <button onClick={() => navigate('/login')} style={{ fontSize: '0.75rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <a href="https://executiveforceai.com/privacy-policy" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Privacy</a>
            <a href="https://executiveforceai.com/terms-conditions" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
